import { ActionIcon, Stack } from '@mantine/core';
import {
  IconBuildingWarehouse,
  IconClipboardList,
  IconContract,
  IconHierarchy3,
  IconHome,
  IconLogout,
  IconPlant,
  IconTestPipe2Filled,
  IconUpload,
  IconUserCircle,
  IconUsers,
  IconListCheck,
  IconClipboardCheck,
  IconMap2,
  IconMapPins,
} from '@tabler/icons-react';
import { getRouteApi } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import NavbarLink from '../navbar-link/navbar-link';
import {
  filterNavbarLinks,
  NavbarLinkConfig,
  NavbarSection,
} from '../navbar-section/navbar-section';

import classes from './app-navbar.module.css';
import { getWipNavLinks } from './wip-nav-links';

import { Permission, useAuth, usePermissions } from '@auth/data-access';
import { LangSelect } from '@ui';

const route = getRouteApi('/_app');

export function AppNavbar() {
  const navigate = route.useNavigate();
  const { logout } = useAuth();
  const { can, isAdmin } = usePermissions();
  const { t } = useTranslation();

  const links: NavbarLinkConfig[] = [
    {
      to: '/os/dashboard',
      label: t('app.nav.dashboard'),
      icon: IconHome,
    },
    {
      to: isAdmin ? '/import' : '/os/import',
      label: t('app.nav.import'),
      icon: IconUpload,
      hasAccess: can(Permission.Import),
    },
    {
      to: '/pratiques',
      label: t('app.nav.pratiques'),
      icon: IconListCheck,
      hasAccess: can(Permission.Pratiques),
    },
    {
      to: '/os/contracts',
      label: t('app.nav.contracts'),
      icon: IconContract,
      hasAccess: can(Permission.Contracts),
    },
    {
      to: '/os/lots',
      label: t('app.nav.lots'),
      icon: IconClipboardCheck,
      hasAccess: can(Permission.LotsValidation),
    },
    {
      to: '/os/lot-reviews',
      label: t('app.nav.lotReviews'),
      icon: IconClipboardCheck,
      hasAccess: can(Permission.OsLotReviews),
    },
    {
      to: '/os/surfaces-map',
      label: t('app.nav.surfacesMap'),
      icon: IconMap2,
      hasAccess: can(Permission.SurfacesMap),
    },
    {
      to: '/os/surfaces-allocation',
      label: t('app.nav.osAllocation'),
      icon: IconMap2,
      hasAccess: can(Permission.SurfacesAllocation),
    },
    {
      to: '/os/validation-forms',
      label: t('app.nav.suiviAgriculteurs'),
      icon: IconClipboardList,
      hasAccess: can(Permission.ValidationForms),
    },
    {
      to: '/sampling-plans',
      label: t('app.nav.samplingPlans'),
      icon: IconPlant,
      hasAccess: can(Permission.SamplingPlans),
    },
    {
      to: '/os/ngp-sampling-assignment',
      label: t('app.nav.ngpSamplerAssignment'),
      icon: IconMapPins,
      hasAccess: can(Permission.NgpSamplerAssignment),
    },
    {
      to: '/ngp-sampling-assignment',
      label: t('app.nav.ngpSamplingAssignment'),
      icon: IconMapPins,
      hasAccess: can(Permission.NgpSamplingAssignment),
    },
    {
      to: '/results',
      label: t('app.nav.results'),
      icon: IconTestPipe2Filled,
      hasAccess: can(Permission.Results),
    },
  ];

  const wipLinks = getWipNavLinks(t, can);

  const bottomLinks: NavbarLinkConfig[] = [
    {
      to: '/organisations',
      label: t('app.nav.organisations'),
      icon: IconHierarchy3,
      hasAccess: can(Permission.Organisations),
    },
    {
      to: '/users',
      label: t('app.nav.users'),
      icon: IconUsers,
      hasAccess: can(Permission.Users),
    },
    {
      to: '/accounts',
      label: t('app.nav.accounts'),
      icon: IconBuildingWarehouse,
      hasAccess: can(Permission.Accounts),
    },
  ];

  const responsiveLinks: NavbarLinkConfig[] = [
    {
      to: '/profile',
      label: t('app.nav.profile'),
      icon: IconUserCircle,
    },
  ];

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
      navigate({ to: '/' });
    }
  };

  return (
    <Stack className={classes.container} justify="space-between">
      <Stack gap="xs">
        <NavbarSection links={links} />
        <NavbarSection
          linkCornerBadge={t('app.nav.wip')}
          links={wipLinks}
          sectionLabel={t('app.nav.wip')}
          showDividerWhenEmpty={isAdmin}
          withDivider
        />
      </Stack>
      <Stack gap="xs">
        <NavbarSection links={bottomLinks} withDivider />
        {filterNavbarLinks(responsiveLinks).map((link) => (
          <NavbarLink hiddenFrom="xs" key={link.label} {...link} />
        ))}
        <Stack align="center" gap="xs" hiddenFrom="md">
          <LangSelect />

          <ActionIcon onClick={handleLogout} size={40} variant="filled">
            <IconLogout style={{ width: '70%', height: '70%' }} />
          </ActionIcon>
        </Stack>
      </Stack>
    </Stack>
  );
}

export default AppNavbar;
